import { createContext, useContext } from 'react';

import { hueClasses, type Hue } from './hues';
import { shortcutLabel, type ShortcutAction } from './shortcuts';

/**
 * Whether a control is sitting on the band — so a `Button` there turns white rather than putting
 * the accent or slate on the theme's deep colour, where neither reads.
 */
const OnBand = createContext(false);

/** For a band drawn elsewhere (the counter's `CounterBand`): its buttons take the band's weights. */
export function OnBandSurface({ children }: { children: React.ReactNode }) {
  return <OnBand.Provider value={true}>{children}</OnBand.Provider>;
}

/**
 * The page heading, as the phone opens a screen: on a band of the theme's deep colour, the title in
 * the phone's serif (owner, 2026-10-23: "App theme options", direction 1).
 *
 * <p>
 * `eyebrow` is the small line above the title (the day, on Today); `children` sit on the band below
 * it (Today's counts). `overlap` lets the block after the heading rise over the band's lower edge,
 * as the phone's cards do — opt-in, because only a card should, never a paragraph. The classes live
 * in `style.css` under the AttireRent banner.
 * </p>
 */
export function PageHeading({
  title,
  subtitle,
  eyebrow,
  actions,
  overlap = false,
  children,
}: {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  actions?: React.ReactNode;
  overlap?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <OnBand.Provider value={true}>
      <div className={`page-band mb-3 ${overlap ? 'page-band-overlap' : ''}`}>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            {eyebrow && <p className="page-eyebrow">{eyebrow}</p>}
            <h1 className="page-title">{title}</h1>
            {subtitle && <p className="page-subtitle">{subtitle}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
        {children && <div className="mt-4">{children}</div>}
      </div>
    </OnBand.Provider>
  );
}

/**
 * The white card everything sits on — the phone's: rounder, lifted by a soft shadow, and a hairline
 * again in dark (`.ar-card` in `style.css`).
 *
 * `flush` drops the padding for a card whose only child is a table — the table draws its own cell
 * padding, and doubling it puts the first column a centimetre from the border.
 */
export function Card({
  children,
  flush = false,
  hue,
  className = '',
}: {
  children: React.ReactNode;
  flush?: boolean;
  /**
   * The 3px top edge, in the colour this card's subject owns — see `components/hues.ts`.
   *
   * <p>
   * One of the three places a hue may be spent, and the quietest: it names the card without taking
   * any of the space the card is for. Cards in a *row* should not take it — six coloured edges side
   * by side read as a paint chart, which is why the Today band spends its hue on the tile alone.
   * </p>
   */
  hue?: Hue;
  className?: string;
}) {
  return (
    <div
      className={`ar-card ${hue ? hueClasses[hue].edge : ''} ${flush ? '' : 'p-4 lg:p-5'} ${className}`}
    >
      {children}
    </div>
  );
}

/**
 * The heading on one section *inside* a card, with room for a control on its right.
 *
 * <p>
 * Distinct from `PageHeading`, which names the screen. This names a block within it — "Customer &
 * dates", "Available garments" — and is the most repeated element on the long data-entry pages.
 * </p>
 *
 * <p>
 * <strong>It was declared privately in three screens before it was declared once</strong>, and two
 * of the three had already drifted apart, which is the same story `components/Field.tsx` tells at
 * length. `RateCardPage` keeps its own for now and deliberately: its version is `text-lg` and
 * baseline-aligned because it heads a page section rather than a card section, and quietly
 * restyling that under a shared name would be a change nobody asked for.
 * </p>
 */
export function SectionTitle({
  children,
  icon,
  hue,
  trailing,
}: {
  children: React.ReactNode;
  /**
   * A glyph in a tinted tile before the heading — the mockup calls it the main friendliness device.
   *
   * <p>
   * A wash tile with the hue as ink, rather than the Today band's filled tile under a white glyph.
   * The band is six of them in a row and needs the weight to read as a set; a section heading is
   * one, inside a card, above a form, and a saturated square there shouts over the fields.
   * </p>
   *
   * <p>
   * Drawn only when a `hue` comes with it. An icon with no colour to sit in is a grey square, which
   * is decoration doing nothing, and this is the component that would spread it across the product.
   * </p>
   */
  icon?: string;
  hue?: Hue;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <h2 className="flex items-center gap-2.5 text-sm font-semibold text-gray-900">
        {icon && hue && (
          <span
            className={`flex size-8 shrink-0 items-center justify-center rounded-md ${hueClasses[hue].wash}`}
            aria-hidden="true"
          >
            <i className={`${icon} text-[15px] ${hueClasses[hue].ink}`} />
          </span>
        )}
        {children}
      </h2>
      {trailing}
    </div>
  );
}

/**
 * A button, in two weights.
 *
 * `primary` is the theme's accent, as the phone draws its main button (owner, 2026-10-23: "colour
 * same as mobile") — it replaced slate-that-hovers-to-teal; white on every accent clears 4.5. On the
 * band it turns white with the band's colour as ink, and the secondary turns translucent, since
 * neither the accent nor a white-edged grey reads on the deep colour. `danger` is not a third weight
 * but a tone on the secondary shape: a destructive action should read as deliberate, not as the
 * loudest thing on the screen.
 *
 * `shortcut` both binds the key and prints it on the face, which is deliberately one prop rather
 * than two: a shortcut nobody is told about is a shortcut nobody uses, and a label with no binding
 * behind it is worse than none. See `shortcuts.ts`.
 */
export function Button({
  variant = 'secondary',
  icon,
  shortcut,
  children,
  ...rest
}: {
  variant?: 'primary' | 'secondary' | 'danger';
  icon?: string;
  shortcut?: ShortcutAction;
  children?: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const onBand = useContext(OnBand);
  const weight = onBand
    ? variant === 'primary'
      ? 'bg-[#ffffff] text-(--color-hero) border border-[#ffffff] font-semibold hover:bg-[#ffffff]/90'
      : variant === 'danger'
        ? 'bg-[#ffffff]/10 border border-[#ffffff]/25 text-[#fecaca] hover:bg-[#ffffff]/20'
        : 'bg-[#ffffff]/10 border border-[#ffffff]/25 text-[#ffffff] hover:bg-[#ffffff]/20'
    : variant === 'primary'
      ? // `text-white` is the template's token: white in light, dark ink in dark, where the accent is a light tint.
        'bg-primary text-white border border-primary hover:bg-primary-hover hover:border-primary-hover'
      : variant === 'danger'
        ? 'bg-white border border-border-color text-danger hover:bg-danger-transparent'
        : 'bg-white border border-border-color text-gray-900 hover:bg-light';

  return (
    <button
      type="button"
      {...rest}
      {...(shortcut ? { 'data-shortcut': shortcut } : {})}
      className={`btn-sm inline-flex cursor-pointer items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50 ${weight} ${rest.className ?? ''}`}
    >
      {icon && <i className={icon} />}
      {children}
      {/*
        `border-current` so one rule reads correctly on the slate primary and the white secondary
        alike, and hidden on phones, where there is no Alt key to press and the label is only taking
        room from the words that say what the button does.
      */}
      {shortcut && (
        <kbd className="rounded border border-current/40 px-1 py-px font-sans text-[10px] leading-none font-medium opacity-70 max-sm:hidden">
          {shortcutLabel(shortcut)}
        </kbd>
      )}
    </button>
  );
}

/**
 * A block of explanation, in one of three tones.
 *
 * Replaces Ant Design's `Alert`, which paints its own surfaces and so was the last thing on these
 * screens still wearing a different design. `info` is the ordinary case and deliberately looks
 * quiet — most of these explain a rule rather than report a problem, and a screen where every
 * explanation is coloured teaches people to stop reading them.
 */
export type NoticeTone = 'info' | 'success' | 'warning' | 'danger';

export function Notice({
  tone = 'info',
  title,
  children,
}: {
  tone?: NoticeTone;
  title: string;
  children?: React.ReactNode;
}) {
  const skin = SKINS[tone];

  return (
    <div className={`rounded-md border p-3 ${skin.box}`}>
      <p className="flex items-center gap-2 text-[13px] font-semibold text-gray-900">
        <i className={`icon ${skin.icon}`} />
        {title}
      </p>
      {children && <div className="mt-1 text-[13px] text-default">{children}</div>}
    </div>
  );
}

/**
 * The template's "Alerts with Icons" set, one entry per tone.
 *
 * A lookup rather than the nested ternary this used to be, because `success` made it a third level
 * and the shape of the thing stopped being readable. The `*-transparent` surfaces and the `icon
 * icon-*` glyphs are the template's own; both flip under `[data-theme="dark"]` on their own.
 */
const SKINS: Record<NoticeTone, { box: string; icon: string }> = {
  info: { box: 'border-border-color bg-light', icon: 'icon-info text-info' },
  success: {
    box: 'border-success/30 bg-success-transparent',
    icon: 'icon-badge-check text-success',
  },
  warning: {
    box: 'border-warning/30 bg-warning-transparent',
    icon: 'icon-triangle-alert text-warning',
  },
  danger: { box: 'border-danger/30 bg-danger-transparent', icon: 'icon-circle-alert text-danger' },
};

/**
 * What a list says when it has nothing in it.
 *
 * Named for the state rather than apologising for it. An empty branch list on a new tenant is the
 * normal beginning of setup, not a failure, and the call to action is what the screen is for.
 */
export function EmptyState({
  icon = 'icon icon-inbox',
  title,
  hint,
  action,
}: {
  icon?: string;
  title: string;
  hint?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-14 text-center">
      <i className={`${icon} text-[28px] text-default opacity-60`} />
      <p className="text-sm font-semibold text-gray-900">{title}</p>
      {hint && <p className="max-w-md text-[13px] text-default">{hint}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
